/**
 * Stable, geographic clustering shared by native and web.
 *
 * Clustering must be a function of inventory + zoom, never of the viewport. If
 * panning changes a cluster, the same physical places appear to exchange cars
 * while the user's finger is moving the map. That was the old behaviour: both
 * renderers performed a screen-space collision pass after every pan, moved
 * pills away from their coordinates and merged anything that did not fit.
 *
 * Supercluster builds one immutable world index. At a given integer zoom its
 * groups and counts are identical no matter where the camera is centred, so a
 * pan is only a translation. A zoom can deliberately split a spatial group;
 * cars at the exact same location remain one honest group and open the picker.
 */
import Supercluster from 'supercluster'
import type { NearbyVehicle } from '../../lib/types/vehicle.types'

type Coordinate = {
  lat: number
  lng: number
}

export type VehicleMapCluster = {
  id: string
  coordinate: Coordinate
  vehicles: NearbyVehicle[]
}

export type VehicleMapMarker = VehicleMapCluster & {
  /** Both geographic and co-located groups open the vehicle picker. */
  kind: 'spatial-cluster' | 'location'
  expansionZoom: number | null
}

type LocationPointProperties = {
  locationId: string
  vehicleCount: number
}

type ClusterAggregateProperties = {
  vehicleCount: number
}

export type VehicleMapClusterIndex = {
  /** Exact locations, used to frame the initial camera. */
  locations: VehicleMapCluster[]
  /** Stable worldwide markers for one integer zoom. */
  markersAtZoom: (zoom: number) => VehicleMapMarker[]
}

const COORDINATE_PRECISION = 5
const MERCATOR_MAX_LATITUDE = 85.05112878
const WORLD_BOUNDS: GeoJSON.BBox = [-180, -MERCATOR_MAX_LATITUDE, 180, MERCATOR_MAX_LATITUDE]

/**
 * Group capsules are real 44pt controls, not passive dots. A 36px radius let
 * two independently-built groups render on top of each other at the city zoom
 * (the marker that happened to mount last swallowed the other one's tap).
 *
 * This is geographic, immutable clustering — it never moves a marker for a
 * viewport or a frame. The larger radius simply makes nearby locations one
 * honest, first-tap vehicle picker before their controls can collide.
 */
const MAP_CLUSTER_RADIUS = 64

/** At neighbourhood scale, expose every location. Never split co-located cars. */
export const MAP_CLUSTER_MAX_ZOOM = 12

function markerKey(location: Coordinate) {
  return `${location.lng.toFixed(COORDINATE_PRECISION)},${location.lat.toFixed(COORDINATE_PRECISION)}`
}

function isMapCoordinate(location: Coordinate): boolean {
  return (
    Number.isFinite(location.lat) &&
    Number.isFinite(location.lng) &&
    location.lat >= -MERCATOR_MAX_LATITUDE &&
    location.lat <= MERCATOR_MAX_LATITUDE &&
    location.lng >= -180 &&
    location.lng <= 180
  )
}

function sortMarkerVehicles(a: NearbyVehicle, b: NearbyVehicle) {
  const titleComparison = a.title.localeCompare(b.title, 'es', { sensitivity: 'base' })
  if (titleComparison !== 0) return titleComparison
  return a.id.localeCompare(b.id)
}

function sortClusters(a: NearbyVehicle[], b: NearbyVehicle[]) {
  if (a.length !== b.length) return b.length - a.length
  const firstA = a[0]
  const firstB = b[0]
  if (!firstA || !firstB) return b.length - a.length
  return sortMarkerVehicles(firstA, firstB)
}

/** How many of these vehicles can be put on a map at all. */
export function countLocatedVehicles(vehicles: NearbyVehicle[]): number {
  let count = 0
  for (const vehicle of vehicles) {
    if (vehicle.homeLocation && isMapCoordinate(vehicle.homeLocation)) count += 1
  }
  return count
}

/**
 * Group vehicles that share a coordinate.
 *
 * This first pass only joins genuinely co-located cars. Spatial grouping is
 * handled by the immutable zoom index below.
 */
export function buildVehicleMapClusters(vehicles: NearbyVehicle[]): VehicleMapCluster[] {
  const groups = new Map<string, NearbyVehicle[]>()

  for (const vehicle of vehicles) {
    if (!vehicle.homeLocation || !isMapCoordinate(vehicle.homeLocation)) continue
    const key = markerKey(vehicle.homeLocation)
    const group = groups.get(key)
    if (group) {
      group.push(vehicle)
    } else {
      groups.set(key, [vehicle])
    }
  }

  const clusters: VehicleMapCluster[] = []

  for (const [id, group] of groups.entries()) {
    const sortedVehicles = [...group].sort(sortMarkerVehicles)
    const firstVehicle = sortedVehicles[0]

    if (!firstVehicle?.homeLocation) continue

    clusters.push({
      id,
      coordinate: firstVehicle.homeLocation,
      vehicles: sortedVehicles,
    })
  }

  return clusters.sort((a, b) => sortClusters(a.vehicles, b.vehicles))
}

/**
 * Mapbox clusters on integer tile zooms. Normalising in one place
 * prevents tiny floating-point differences between platforms from changing a
 * group at the edge of a zoom level.
 */
export function normalizeMapClusterZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) return 0
  return Math.max(0, Math.min(MAP_CLUSTER_MAX_ZOOM + 1, Math.floor(zoom + 1e-6)))
}

/** Build once per inventory change; camera movement never rebuilds this index. */
export function buildVehicleMapClusterIndex(vehicles: NearbyVehicle[]): VehicleMapClusterIndex {
  const locations = buildVehicleMapClusters(vehicles)
  const locationsById = new Map(locations.map((location) => [location.id, location]))

  // Supercluster is intentionally greedy. A geographic order keeps topology a
  // function of geometry: changing a title or adding another car at an existing
  // point cannot make an unrelated neighbouring cluster choose a new parent.
  const indexLocations = [...locations].sort((a, b) => {
    const longitudeComparison = a.coordinate.lng - b.coordinate.lng
    if (longitudeComparison !== 0) return longitudeComparison
    const latitudeComparison = a.coordinate.lat - b.coordinate.lat
    if (latitudeComparison !== 0) return latitudeComparison
    return a.id.localeCompare(b.id)
  })

  const points: Array<Supercluster.PointFeature<LocationPointProperties>> = indexLocations.map(
    (location) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [location.coordinate.lng, location.coordinate.lat],
      },
      properties: {
        locationId: location.id,
        vehicleCount: location.vehicles.length,
      },
    }),
  )

  const tree = new Supercluster<LocationPointProperties, ClusterAggregateProperties>({
    radius: MAP_CLUSTER_RADIUS,
    maxZoom: MAP_CLUSTER_MAX_ZOOM,
    minPoints: 2,
    map: (properties) => ({ vehicleCount: properties.vehicleCount }),
    reduce: (aggregate, properties) => {
      aggregate.vehicleCount += properties.vehicleCount
    },
  }).load(points)

  const markersAtZoom = (zoom: number): VehicleMapMarker[] => {
    const integerZoom = normalizeMapClusterZoom(zoom)
    const features = tree.getClusters(WORLD_BOUNDS, integerZoom)
    const markers: VehicleMapMarker[] = []

    for (const feature of features) {
      const lng = feature.geometry.coordinates[0]
      const lat = feature.geometry.coordinates[1]
      if (typeof lng !== 'number' || typeof lat !== 'number') continue
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue

      if ('cluster' in feature.properties && feature.properties.cluster) {
        const clusterId = feature.properties.cluster_id
        const locationIds = tree
          .getLeaves(clusterId, Infinity)
          .map((leaf) => leaf.properties.locationId)
          .sort()
        const vehiclesInCluster = locationIds.flatMap((id) => locationsById.get(id)?.vehicles ?? [])

        if (vehiclesInCluster.length === 0) continue
        markers.push({
          // Supercluster IDs depend on the whole tree. Geographic membership
          // also stays stable when an unrelated location is added on refetch.
          id: `spatial-${locationIds.join(';')}`,
          kind: 'spatial-cluster',
          coordinate: { lng, lat },
          vehicles: vehiclesInCluster,
          expansionZoom: tree.getClusterExpansionZoom(clusterId),
        })
        continue
      }

      if (!('locationId' in feature.properties)) continue
      const location = locationsById.get(feature.properties.locationId)
      if (!location) continue
      markers.push({
        ...location,
        kind: 'location',
        expansionZoom: null,
      })
    }

    markers.sort((a, b) => {
      const vehicleComparison = b.vehicles.length - a.vehicles.length
      if (vehicleComparison !== 0) return vehicleComparison
      return a.id.localeCompare(b.id)
    })

    return markers
  }

  return { locations, markersAtZoom }
}
